import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, AlertTriangle, CheckCircle2, HelpCircle, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  useGetCourierCollectionChecks,
  useUpdateCourierCollectionLookup,
  getGetCourierCollectionChecksQueryKey,
  type CourierCollectionCheck,
  type CourierCollectionCheckReport,
  type CourierCollectionLookupInput,
  type CourierCollectionLookupOverride,
} from "@workspace/api-client-react";
import { normalisePostcode } from "./collection-fees";

/** Couriers with third-party collection calculations (never Royal Mail). */
export const COLLECTION_CHECK_COURIERS = ["DHL", "EVRIPREMIUM"];
export const supportsCollectionChecks = (code: string | null | undefined) =>
  !!code && COLLECTION_CHECK_COURIERS.includes(code.trim().toUpperCase());

export const SOURCE_LABELS: Record<string, string> = {
  saved_mapping: "Saved locality postcode",
  dhl_servicing_locality: "DHL servicing locality",
  dhl_servicing_and_physical_evidence: "DHL servicing + physical locality evidence",
  verified_consignment_lookup: "Operator-verified full consignment lookup",
  operator_override: "Operator-evidenced origin postcode",
  unavailable: "Unavailable",
};
export const sourceLabel = (s: string | null | undefined) =>
  !s ? "Unavailable" : SOURCE_LABELS[s] ?? s.replace(/_/g, " ").replace(/^./, (m) => m.toUpperCase());

const money = (n: number | null) => (n == null ? "Unavailable" : `£${n.toFixed(2)}`);

const STATUS: Record<CourierCollectionCheck["status"], { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  passed: { label: "Passed", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200", Icon: CheckCircle2 },
  overcharge: { label: "Overcharge", cls: "bg-rose-50 text-rose-800 ring-rose-200", Icon: AlertTriangle },
  review: { label: "Needs review", cls: "bg-amber-50 text-amber-800 ring-amber-200", Icon: HelpCircle },
  outbound: { label: "Outbound", cls: "bg-slate-100 text-slate-700 ring-slate-200", Icon: ArrowRight },
};
const ROUTE: Record<CourierCollectionCheck["route"], string> = {
  leamington: "Back to depot",
  third_party: "Third party to third party",
  outbound: "Outbound",
  unknown: "Route unknown",
};

export type LookupDraft = { consignmentNumber: string; postcode: string; note: string };

export function validateLookup(d: LookupDraft): { input: CourierCollectionLookupInput | null; errors: string[] } {
  const errors: string[] = [];
  const c = d.consignmentNumber.replace(/\s+/g, "");
  if (c && !/^\d{14}$/.test(c)) errors.push("Lookup consignment must be the full verified 14-digit number. Do not pad or guess digits.");
  const pRaw = d.postcode.trim();
  const p = pRaw ? normalisePostcode(pRaw) : null;
  if (pRaw && !p) errors.push("Origin postcode must be a full known UK postcode, for example CV31 1XT.");
  const note = d.note.trim();
  if (note.length < 5) errors.push("Explain the evidence for this correction (at least 5 characters).");
  if (note.length > 1000) errors.push("Note must be 1000 characters or fewer.");
  return { input: errors.length ? null : { consignmentNumber: c || null, postcode: p, note }, errors };
}

function Field({ label, value, mono, testId }: { label: string; value: React.ReactNode; mono?: boolean; testId?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-duffells-grey/50">{label}</dt>
      <dd className={`text-sm text-duffells-grey truncate ${mono ? "font-mono" : ""}`} data-testid={testId}>{value}</dd>
    </div>
  );
}

function LookupEditor({ check, override, saving, error, onSave, onCancel }: {
  check: CourierCollectionCheck; override?: CourierCollectionLookupOverride; saving: boolean; error: string | null;
  onSave: (i: CourierCollectionLookupInput) => void; onCancel: () => void;
}) {
  const [d, setD] = useState<LookupDraft>({
    consignmentNumber: override?.consignmentNumber ?? "",
    postcode: override?.postcode ?? "",
    note: override?.note ?? "",
  });
  const { input, errors } = validateLookup(d);
  const id = check.invoiceLineId;
  const inp = "border border-slate-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-duffells-orange/40 disabled:opacity-60";
  return (
    <form
      className="mt-3 rounded-lg border border-duffells-orange/30 bg-orange-50/40 p-3 space-y-2"
      onSubmit={(e) => { e.preventDefault(); if (input) onSave(input); }}
      data-testid={`form-lookup-${id}`}
    >
      <p className="text-xs text-duffells-grey/70">
        Original invoice fields are never changed. Leave a field blank to clear its correction. Applies to every line on this invoice with the same original consignment.
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-xs text-duffells-grey/70">Verified lookup consignment (14 digits)
          <input className={`${inp} font-mono`} inputMode="numeric" maxLength={20} disabled={saving} value={d.consignmentNumber}
            onChange={(e) => setD({ ...d, consignmentNumber: e.target.value })} data-testid={`input-lookup-consignment-${id}`} />
        </label>
        <label className="text-xs text-duffells-grey/70">Known origin postcode
          <input className={`${inp} font-mono uppercase`} maxLength={10} disabled={saving} value={d.postcode}
            onChange={(e) => setD({ ...d, postcode: e.target.value })} data-testid={`input-lookup-postcode-${id}`} />
        </label>
      </div>
      <label className="block text-xs text-duffells-grey/70">Evidence and source (required)
        <textarea className={inp} rows={2} maxLength={1000} disabled={saving} value={d.note}
          onChange={(e) => setD({ ...d, note: e.target.value })} data-testid={`input-lookup-note-${id}`} />
      </label>
      {errors.length > 0 && <ul className="text-xs text-red-700 space-y-0.5" data-testid={`text-lookup-errors-${id}`}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
      {error && <p role="alert" className="text-xs text-red-700" data-testid={`text-lookup-save-error-${id}`}>Save failed: {error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onCancel} data-testid={`button-cancel-lookup-${id}`}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving || !input} data-testid={`button-save-lookup-${id}`}>
          {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Save correction
        </Button>
      </div>
    </form>
  );
}

function CheckCard({ c, override, isAdmin, editing, saving, error, onEdit, onCancel, onSave }: {
  c: CourierCollectionCheck; override?: CourierCollectionLookupOverride; isAdmin: boolean; editing: boolean;
  saving: boolean; error: string | null; onEdit: () => void; onCancel: () => void; onSave: (i: CourierCollectionLookupInput) => void;
}) {
  const s = STATUS[c.status];
  const id = c.invoiceLineId;
  const corrected = !!c.lookupConsignmentNumber && c.lookupConsignmentNumber !== c.consignmentNumber;
  const rows: [string, number | null][] = [
    [`Collection${c.collectionZone ? ` (zone ${c.collectionZone})` : ""}`, c.collectionFee],
    [c.route === "third_party" ? `Delivery${c.deliveryZone ? ` (zone ${c.deliveryZone})` : ""}` : "Depot delivery", c.deliveryFee],
    ["Insurance", c.insuranceFee],
    ["Congestion", c.congestionFee],
  ];
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`card-collection-check-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${s.cls}`} data-testid={`status-collection-check-${id}`}>
            <s.Icon className="w-3 h-3" aria-hidden />{s.label}
          </span>
          <span className="text-xs text-duffells-grey/60">{ROUTE[c.route]}</span>
          {c.parcelCount != null && <span className="text-xs text-duffells-grey/60">{c.parcelCount} piece{c.parcelCount === 1 ? "" : "s"}</span>}
        </div>
        {isAdmin && !editing && (
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`button-edit-lookup-${id}`}>
            <Pencil className="w-3 h-3 mr-1" />{override ? "Edit correction" : "Correct lookup"}
          </Button>
        )}
      </div>
      <p className="text-xs text-duffells-grey/80 mt-2" data-testid={`text-collection-reason-${id}`}>{c.reason}</p>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 mt-3">
        <Field label="Invoice consignment" mono value={c.consignmentNumber ?? "Not on invoice"} testId={`text-original-consignment-${id}`} />
        <Field label={corrected ? "Lookup ID (corrected)" : "Lookup ID"} mono value={c.lookupConsignmentNumber ?? "Unavailable"} testId={`text-lookup-consignment-${id}`} />
        <Field label="Origin postcode" mono value={c.postcode ?? "Unavailable"} testId={`text-origin-postcode-${id}`} />
        <Field label="Destination" mono value={c.destinationPostcode || "Unavailable"} />
        <Field label="Servicing locality" value={c.servicingLocality ?? "Unavailable"} />
        <Field label="Physical locality" value={c.physicalLocality ?? "Unavailable"} />
        <Field label="Postcode source" value={sourceLabel(c.source)} testId={`text-collection-source-${id}`} />
      </dl>

      <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm tabular-nums" data-testid={`breakdown-collection-${id}`}>
        {rows.map(([l, v]) => (
          <div key={l} className="flex justify-between text-xs text-duffells-grey/70"><span>{l}</span><span>{money(v)}</span></div>
        ))}
        <div className="flex justify-between border-t border-slate-200 mt-1 pt-1 font-medium"><span>Expected</span><span data-testid={`text-expected-${id}`}>{money(c.expectedTotal)}</span></div>
        <div className="flex justify-between"><span>Invoiced <span className="text-[11px] text-duffells-grey/50">(excl. long length)</span></span><span data-testid={`text-actual-${id}`}>{money(c.actual)}</span></div>
        <div className={`flex justify-between font-semibold ${c.difference && c.difference > 0 ? "text-rose-700" : ""}`}>
          <span>Difference</span><span data-testid={`text-difference-${id}`}>{c.difference == null ? "Unavailable" : `${c.difference > 0 ? "+" : ""}£${c.difference.toFixed(2)}`}</span>
        </div>
      </div>

      {override && !editing && (
        <div className="mt-2 text-xs border-l-2 border-duffells-orange pl-2 text-duffells-grey/80" data-testid={`text-override-${id}`}>
          Saved correction: {override.consignmentNumber ? <span className="font-mono">{override.consignmentNumber}</span> : "original ID"}
          {" / "}{override.postcode ? <span className="font-mono">{override.postcode}</span> : "no postcode"}. {override.note}
        </div>
      )}
      {editing && <LookupEditor check={c} override={override} saving={saving} error={error} onSave={onSave} onCancel={onCancel} />}
    </li>
  );
}

/** Pure view; used by tests and the connected section. */
export function CollectionChecksView({ report, isAdmin, editingId, setEditingId, savingId, error, onSave }: {
  report: CourierCollectionCheckReport; isAdmin: boolean; editingId: number | null; setEditingId: (id: number | null) => void;
  savingId: number | null; error: string | null; onSave: (lineId: number, input: CourierCollectionLookupInput) => void;
}) {
  const byLine = new Map(report.overrides.map((o) => [o.invoiceLineId, o]));
  const byOriginal = new Map(report.overrides.filter((o) => o.originalConsignmentNumber).map((o) => [o.originalConsignmentNumber!, o]));
  const counts = report.checks.reduce<Record<string, number>>((a, c) => ({ ...a, [c.status]: (a[c.status] ?? 0) + 1 }), {});
  return (
    <section aria-label="Collection calculations" className="space-y-3" data-testid="section-collection-checks">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-duffells-grey">Collection calculations ({report.checks.length})</h3>
        <span className="text-xs text-duffells-grey/60" data-testid="text-collection-checked-at">
          {report.checkedAt ? `From audit ${new Date(report.checkedAt).toLocaleString("en-GB")}` : "No saved audit snapshot"}
          {report.checks.length > 0 && ` · ${counts.passed ?? 0} passed, ${counts.overcharge ?? 0} overcharge, ${counts.review ?? 0} review`}
        </span>
      </div>
      {report.stale && (
        <div role="status" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="banner-collection-stale">
          <RefreshCw className="w-4 h-4 shrink-0" aria-hidden />
          <span>These results predate saved lookup corrections and may be out of date. Re-run the audit to recalculate. Corrections do not re-run it automatically.</span>
        </div>
      )}
      <ul className="text-xs text-duffells-grey/60 space-y-0.5 list-disc list-inside" data-testid="text-collection-notes">
        <li>Invoiced amounts exclude long-length charges, which are checked independently and stay flagged separately even when the collection fee matches.</li>
        <li>A physical locality can corroborate a regional servicing label; the original servicing locality is still shown.</li>
        <li>An origin postcode representing a locality is not street-level proof of the collection address.</li>
      </ul>
      {report.checks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-duffells-grey/60" data-testid="empty-collection-checks">
          {report.checkedAt
            ? "No collection calculations are saved for this audit. Either it had no third-party collection lines, or it was run before calculations were stored; re-run the audit if you expect some."
            : "Run the audit to calculate third-party collection charges."}
        </div>
      ) : (
        <ul className="space-y-2">
          {report.checks.map((c) => (
            <CheckCard key={c.invoiceLineId} c={c}
              override={byLine.get(c.invoiceLineId) ?? (c.consignmentNumber ? byOriginal.get(c.consignmentNumber) : undefined)}
              isAdmin={isAdmin} editing={editingId === c.invoiceLineId} saving={savingId === c.invoiceLineId}
              error={editingId === c.invoiceLineId ? error : null}
              onEdit={() => setEditingId(c.invoiceLineId)} onCancel={() => setEditingId(null)}
              onSave={(i) => onSave(c.invoiceLineId, i)} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Connected section. Render with key={invoiceId}. */
export function CollectionChecksSection({ invoiceId, isAdmin }: { invoiceId: number; isAdmin: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const q = useGetCourierCollectionChecks(invoiceId, {
    query: { queryKey: getGetCourierCollectionChecksQueryKey(invoiceId), enabled: !!invoiceId },
  });
  const save = useUpdateCourierCollectionLookup({
    mutation: {
      onSuccess: (data, vars) => {
        qc.setQueryData(getGetCourierCollectionChecksQueryKey(vars.id), data);
        qc.invalidateQueries({ queryKey: getGetCourierCollectionChecksQueryKey(vars.id) });
        qc.invalidateQueries({ queryKey: ["courier-invoices"] });
        if (!mounted.current) return;
        setEditingId(null); setError(null);
        toast({ title: "Lookup correction saved", description: "Re-run the audit to recalculate collection charges." });
      },
      onError: (err: any) => { if (mounted.current) setError(err?.data?.error || err?.message || "Unknown error"); },
    },
  });

  if (q.isLoading) return <div className="h-32 rounded-lg border border-slate-200 bg-slate-50 animate-pulse" data-testid="skeleton-collection-checks" />;
  if (q.isError || !q.data) return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm flex items-center justify-between">
      <span className="text-red-700">Could not load collection calculations.</span>
      <Button size="sm" variant="outline" onClick={() => q.refetch()} data-testid="button-retry-collection-checks">Retry</Button>
    </div>
  );
  return (
    <CollectionChecksView report={q.data} isAdmin={isAdmin} editingId={editingId}
      setEditingId={(id) => { setEditingId(id); setError(null); }}
      savingId={save.isPending ? save.variables?.lineId ?? null : null} error={error}
      onSave={(lineId, data) => save.mutate({ id: invoiceId, lineId, data })} />
  );
}
